/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import { getBreadcrumb } from "./functions";
import { stripHtml } from "../utils/stripHtml";
import { normalizeUrl } from "../utils/normalizeUrl";

const hasBreadcrumbInSchema = (rawSchema) => {
  if (!rawSchema) return false;
  return rawSchema.includes("BreadcrumbList");
};

const SEO = ({
  description,
  lang,
  meta,
  title,
  post,
  seoOverrides = {},
}) => {
  const { site, wp, wpUser } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
          }
        }
        wp {
          generalSettings {
            title
            description
          }
        }
        wpUser {
          twitter: name
        }
      }
    `
  );

  const siteUrl = site.siteMetadata.siteUrl.replace(/\/$/, "");
  const siteTitle = wp.generalSettings?.title;
  const postPath = post?.uri ? normalizeUrl(post.uri) : "/";
  const fallbackCanonical = `${siteUrl}${postPath}`;

  const defaultTitle =
    seoOverrides.title ||
    post?.seo?.title ||
    title ||
    siteTitle;

  const metaDescription =
    seoOverrides.metaDesc ||
    post?.seo?.metaDesc ||
    stripHtml(description) ||
    wp.generalSettings?.description;

  const ogTitle = post?.seo?.opengraphTitle || defaultTitle;
  const ogDescription =
    post?.seo?.opengraphDescription || metaDescription;
  const ogImage =
    post?.seo?.opengraphImage?.localFile?.childImageSharp?.fluid?.src || "";
  const ogType =
    seoOverrides.ogType ||
    (post?.__typename === "WpPost" ? "article" : "website");
  const ogUrl =
    seoOverrides.canonical ||
    post?.seo?.canonical ||
    post?.seo?.opengraphUrl ||
    fallbackCanonical;

  const canonicalUrl =
    seoOverrides.canonical ||
    post?.seo?.canonical ||
    fallbackCanonical;

  const twitterCard = ogImage ? "summary_large_image" : "summary";

  const indexDirective =
    post?.seo?.metaRobotsNoindex === "noindex" ? "noindex" : "index";
  const followDirective =
    post?.seo?.metaRobotsNofollow === "nofollow" ? "nofollow" : "follow";
  const robotsContent = `${indexDirective}, ${followDirective}`;
  const hasCustomRobots =
    indexDirective !== "index" || followDirective !== "follow";

  const yoastSchemaRaw = post?.seo?.schema?.raw;
  const showCustomBreadcrumb =
    !yoastSchemaRaw || !hasBreadcrumbInSchema(yoastSchemaRaw);

  const prevUrl = seoOverrides.prev
    ? `${siteUrl}${seoOverrides.prev}`
    : null;
  const nextUrl = seoOverrides.next
    ? `${siteUrl}${seoOverrides.next}`
    : null;

  return (
    <Helmet
      htmlAttributes={{
        lang: lang || "fr",
      }}
      title={defaultTitle}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        ...(hasCustomRobots
          ? [
              {
                name: `robots`,
                content: robotsContent,
              },
            ]
          : []),
        {
          property: `og:title`,
          content: ogTitle,
        },
        {
          property: `og:description`,
          content: ogDescription,
        },
        ...(ogImage
          ? [
              {
                property: `og:image`,
                content: ogImage,
              },
            ]
          : []),
        {
          property: `og:type`,
          content: ogType,
        },
        {
          property: `og:url`,
          content: ogUrl,
        },
        {
          name: `twitter:card`,
          content: twitterCard,
        },
        ...(ogImage
          ? [
              {
                name: `twitter:image`,
                content: ogImage,
              },
            ]
          : []),
        {
          name: `twitter:creator`,
          content: wpUser?.twitter || ``,
        },
        {
          name: `twitter:title`,
          content: ogTitle,
        },
        {
          name: `twitter:description`,
          content: ogDescription,
        },
      ].concat(meta)}
    >
      <link rel="canonical" href={canonicalUrl} />
      {prevUrl && <link rel="prev" href={prevUrl} />}
      {nextUrl && <link rel="next" href={nextUrl} />}
      {yoastSchemaRaw && (
        <script type="application/ld+json">{yoastSchemaRaw}</script>
      )}
      {showCustomBreadcrumb && (
        <script type="application/ld+json">{getBreadcrumb(post)}</script>
      )}
    </Helmet>
  );
};

SEO.defaultProps = {
  lang: `fr`,
  meta: [],
  description: ``,
  seoOverrides: {},
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  seoOverrides: PropTypes.shape({
    title: PropTypes.string,
    metaDesc: PropTypes.string,
    canonical: PropTypes.string,
    prev: PropTypes.string,
    next: PropTypes.string,
    ogType: PropTypes.string,
  }),
};

export default SEO;
